import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Minimal markdown-ish renderer for the streamed advice.
const renderInline = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="font-semibold text-[#1A231F]">{p.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{p}</span>
    )
  );
};

const Rendered = ({ text }) => {
  const lines = text.split("\n");
  return (
    <div className="space-y-1.5">
      {lines.map((line, i) => {
        const t = line.trim();
        if (!t) return <div key={i} className="h-1" />;
        if (t.startsWith("## "))
          return <h4 key={i} className="font-heading font-bold text-[#D96B43] text-sm uppercase tracking-wide pt-2">{renderInline(t.slice(3))}</h4>;
        if (t.startsWith("# "))
          return <h4 key={i} className="font-heading font-bold text-[#1A231F] text-base pt-2">{renderInline(t.slice(2))}</h4>;
        if (t.startsWith("- ") || t.startsWith("* "))
          return (
            <div key={i} className="flex gap-2 text-sm text-[#5C6A64] leading-relaxed">
              <span className="text-[#D99B26] mt-1">•</span>
              <span>{renderInline(t.slice(2))}</span>
            </div>
          );
        return <p key={i} className="text-sm text-[#5C6A64] leading-relaxed">{renderInline(t)}</p>;
      })}
    </div>
  );
};

export const AIAdvisor = ({ inputs, result }) => {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    setText("");
    try {
      const payload = {
        inputs,
        summary: {
          retirementCorpus: result.retirementCorpus,
          firstYearExpense: result.firstYearExpense,
          swr: result.swr,
          depletionAge: result.depletionAge,
          finalCorpus: result.finalCorpus,
          totalWithdrawn: result.totalWithdrawn,
          status: result.status,
          retirementYears: result.retirementYears,
          preReturn: result.preReturn,
        },
      };
      const res = await fetch(`${API}/advisor`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok || !res.body) throw new Error("advisor failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        setText((prev) => prev + decoder.decode(value, { stream: true }));
      }
    } catch (e) {
      setText("Sorry, the advisor is unavailable right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#E8E3D9] bg-gradient-to-br from-[#F5F2EB] to-white shadow-sm p-6"
      data-testid="ai-advisor">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-lg bg-[#1E2B25] flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-[#D99B26]" />
          </span>
          <div>
            <h3 className="font-heading text-lg font-semibold text-[#1A231F] leading-tight">AI Retirement Advisor</h3>
            <p className="text-xs text-[#8A9790]">Personalised assessment of your plan</p>
          </div>
        </div>
        <Button data-testid="generate-advice-btn" onClick={generate} disabled={loading}
          className="bg-[#D96B43] hover:bg-[#c25b36] text-white rounded-full px-5">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Get Advice"}
        </Button>
      </div>

      <AnimatePresence>
        {(text || loading) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mt-2 rounded-xl bg-white border border-[#E8E3D9] p-4"
            data-testid="advice-content"
          >
            {text ? <Rendered text={text} /> : (
              <div className="flex items-center gap-2 text-sm text-[#8A9790]">
                <Loader2 className="h-4 w-4 animate-spin" /> Analysing your plan…
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {!text && !loading && (
        <p className="text-sm text-[#8A9790] mt-1">
          Tap <span className="font-semibold text-[#D96B43]">Get Advice</span> for a tailored review covering safe
          withdrawal rate, sequence-of-returns risk and concrete adjustments.
        </p>
      )}
    </div>
  );
};
